// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    DoNotFollowLinks,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Page from "@insite/mobius/Page";
import { getCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import loadEvents from "@vanmeter/App/Handlers/EventList/LoadEvents";
import updateFields from "@vanmeter/App/Handlers/EventList/UpdateFields";
import { IEventListParams } from "@vanmeter/App/Services/EventListService";
import { CustomAppState, getEventListDataView } from "@vanmeter/App/Store/Reducers";
import { xScreen } from "@vanmeter/XDL/Utilities";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);

    return {
        parameters: state.xEventList.params,
        location,
        eventsDataView: getEventListDataView(state),
    };
};

const mapDispatchToProps = {
    loadEvents,
    updateFields,
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps &
    PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface VmiEventsListPage {
    container?: InjectableCss;
    contentWrapper?: InjectableCss;
    leftSection?: InjectableCss;
}
export const styles: VmiEventsListPage = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 64px;
        `,
    },
    contentWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 1fr minmax(0, 376px);
            ${xScreen("lte", "lg")} {
                grid-template-columns: 1fr minmax(0, 317px);
            }
            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr 1fr;
            }
            ${xScreen("lt", "sm")} {
                grid-template-columns: 1fr;
            }
            grid-gap: 50px 22px;
        `,
    },
    leftSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 32px;
        `,
    },
};

const VmiEventsListPage: React.FunctionComponent<Props> = ({
    id,
    location,
    eventsDataView,
    loadEvents,
    updateFields,
    parameters,
    history,
}) => {
    let firstLoad = false;
    useEffect(() => {
        firstLoad = true;
        const pageSizeCookie = getCookie("EventsList-PageSize");
        const pageSize = pageSizeCookie ? parseInt(pageSizeCookie, 10) : undefined;
        if (location.search) {
            const apiParameters = parseQueryString<IEventListParams>(location.search);
            if (pageSize) {
                apiParameters.pageSize = pageSize;
            }
            updateFields({ ...apiParameters, type: "Replace" });
        } else {
            updateFields({ type: "Initialize", pageSize, parentId: undefined });
        }
        return () => {
            firstLoad = false;
        };
    }, []);

    useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify({ ...parameters, parentId: undefined });
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [parameters]);

    useEffect(() => {
        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
        if (!firstLoad && !eventsDataView.value && !eventsDataView.isLoading) {
            loadEvents(parameters);
        }
    });

    return (
        <Page>
            <StyledWrapper {...styles.container}>
                <XngZone contentId={id} zoneName="banner" hint="banner" />
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.leftSection}>
                        <XngZone contentId={id} zoneName="content" hint="Content" />
                        <XngZone contentId={id} zoneName="eventList" hint="Event List" />
                    </StyledWrapper>
                    <XngZone contentId={id} zoneName="rightSection" hint="Right Section" />
                </StyledWrapper>
            </StyledWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(VmiEventsListPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        allowedChildren: ["Page", "VmiEventCoursePage", "VmiEventDetailsPage", "VmiFolderPage"],
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            DoNotFollowLinks,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
        ],
    },
};
export default pageModule;
/**
 * @deprecated Use string literal "VmiEventsListPage" instead of this constant.
 */
export const VmiEventsListPageContext = "VmiEventsListPage";
